import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";

function toDateStr(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date;
}
function startOfMonthGrid(d) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeek(first);
}

export default function AdminCalendar() {
  const { t, lang } = useLanguage();
  const [view, setView] = useState("day");
  const [current, setCurrent] = useState(new Date());
  const [bookings, setBookings] = useState([]);

  const range = useMemo(() => {
    if (view === "day") return { from: current, to: current };
    if (view === "week") {
      const start = startOfWeek(current);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return { from: start, to: end };
    }
    const start = startOfMonthGrid(current);
    const end = new Date(start); end.setDate(end.getDate() + 41);
    return { from: start, to: end };
  }, [view, current]);

  useEffect(() => {
    const load = () => {
      api.get("/admin/bookings", { params: { date_from: toDateStr(range.from), date_to: toDateStr(range.to), status: "confirmed" } })
        .then(({ data }) => setBookings(data)).catch(() => setBookings([]));
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [range]);

  const byDate = useMemo(() => {
    const map = {};
    bookings.forEach((b) => { (map[b.date] = map[b.date] || []).push(b); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [bookings]);

  const navigate = (dir) => {
    const d = new Date(current);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrent(d);
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(current);
    return [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [current]);

  const monthDays = useMemo(() => {
    const start = startOfMonthGrid(current);
    return [...Array(42)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [current]);

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-white">{t("admin.calendar.title")}</h1>
        <div className="flex items-center gap-2">
          {["day", "week", "month"].map((v) => (
            <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)} className={`px-4 py-1.5 text-sm rounded-md ${view === v ? "bg-gold text-white" : "bg-white/5 text-white/60"}`}>
              {t(`admin.calendar.${v}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <button data-testid="calendar-prev" onClick={() => navigate(-1)} className="p-2 border border-white/10 rounded-md text-white/70 hover:border-gold"><ChevronLeft size={16} /></button>
        <button data-testid="calendar-today" onClick={() => setCurrent(new Date())} className="text-sm text-gold">{t("admin.calendar.today")}</button>
        <button data-testid="calendar-next" onClick={() => navigate(1)} className="p-2 border border-white/10 rounded-md text-white/70 hover:border-gold"><ChevronRight size={16} /></button>
        <span className="text-white/60 text-sm">{current.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { month: "long", year: "numeric", ...(view === "day" ? { day: "numeric" } : {}) })}</span>
      </div>

      {view === "day" && (
        <div data-testid="calendar-day-view" className="bg-[#141414] border border-white/10 rounded-lg divide-y divide-white/5">
          {(byDate[toDateStr(current)] || []).length === 0 && <p className="p-6 text-white/40 text-sm">{t("admin.calendar.noAppointments")}</p>}
          {(byDate[toDateStr(current)] || []).map((b) => <AppointmentRow key={b.id} b={b} lang={lang} />)}
        </div>
      )}

      {view === "week" && (
        <div data-testid="calendar-week-view" className="grid grid-cols-1 sm:grid-cols-7 gap-3">
          {weekDays.map((d) => (
            <div key={d.toISOString()} className="bg-[#141414] border border-white/10 rounded-lg p-3 min-h-[120px]">
              <p className="text-xs text-white/40 mb-2">{d.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { weekday: "short", day: "numeric" })}</p>
              <div className="space-y-1.5">
                {(byDate[toDateStr(d)] || []).map((b) => (
                  <div key={b.id} data-testid={`week-appt-${b.id}`} className="bg-gold/10 border border-gold/20 rounded px-2 py-1 text-xs text-gold">
                    {b.start_time} {b.customer_name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "month" && (
        <div data-testid="calendar-month-view" className="grid grid-cols-7 gap-2">
          {monthDays.map((d) => {
            const items = byDate[toDateStr(d)] || [];
            const inMonth = d.getMonth() === current.getMonth();
            return (
              <button key={d.toISOString()} data-testid={`month-day-${toDateStr(d)}`} onClick={() => { setCurrent(d); setView("day"); }}
                className={`text-left p-2 rounded-lg border min-h-[70px] ${inMonth ? "border-white/10 bg-[#141414]" : "border-white/5 bg-transparent opacity-40"}`}>
                <p className="text-xs text-white/60">{d.getDate()}</p>
                {items.length > 0 && <p className="text-[10px] text-gold mt-1">{items.length} afspr.</p>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AppointmentRow = ({ b, lang }) => (
  <div data-testid={`day-appt-${b.id}`} className="flex items-center justify-between px-5 py-3">
    <div className="flex items-center gap-4">
      <span className="text-gold text-sm w-14">{b.start_time}</span>
      <div>
        <p className="text-white text-sm">{b.customer_name}</p>
        <p className="text-white/40 text-xs">{lang === "nl" ? b.service_name_nl : b.service_name_en}</p>
      </div>
    </div>
    <span className="text-white/40 text-xs">{b.end_time}</span>
  </div>
);
